import { Stack, useTheme } from "@mui/material";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { CustomerUserNotificationSettingsItems } from "./components";
import {
  useGetCustomerNotificationsSettings,
  useResetCustomerNotificationsSettings,
} from "controller";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

export const CustomerUserNotifications = () => {
  const theme = useTheme();
  const { id: customerId } = useParams();
  const {
    mutateAsync: resetCustomerNotificationsSettings,
    isLoading: isResetting,
  } = useResetCustomerNotificationsSettings();
  const { data: initialCustomerUserNotificationSettings } =
    useGetCustomerNotificationsSettings(customerId || "");
  const initialCustomerUserNotificationSettingsArr: any[] =
    initialCustomerUserNotificationSettings?.result || [];

  const getInitialCustomerUserSettings = ({
    notifyType,
    notifyThrough,
  }: {
    notifyType: string;
    notifyThrough:
      | "email"
      | "agent_app_desktop"
      | "agent_mobile_app"
      | "teams_app";
  }) => {
    return (
      initialCustomerUserNotificationSettingsArr
        ?.find((item: any) => item?.notify_type === notifyType)
        ?.notify_method?.find(
          (item: any) => item?.notify_through === notifyThrough,
        )?.roles || []
    );
  };
  const getInitialCustomerUserNotificationSoundSettings = (
    notifyType: string,
  ) =>
    initialCustomerUserNotificationSettingsArr?.find(
      (item: any) => item?.notify_type === notifyType,
    )?.notification_sound;

  const defaultValues = {
    customers: {
      ticket: {
        new_ticket: {
          email:
            getInitialCustomerUserSettings({
              notifyType: "new_ticket",
              notifyThrough: "email",
            }) || [],
          agent_app_desktop:
            getInitialCustomerUserSettings({
              notifyType: "new_ticket",
              notifyThrough: "agent_app_desktop",
            }) || [],
          agent_mobile_app:
            getInitialCustomerUserSettings({
              notifyType: "new_ticket",
              notifyThrough: "agent_mobile_app",
            }) || [],
          teams_app:
            getInitialCustomerUserSettings({
              notifyType: "new_ticket",
              notifyThrough: "teams_app",
            }) || [],
          notification_sound:
            getInitialCustomerUserNotificationSoundSettings("new_ticket") ??
            false,
        },
        update_ticket: {
          email:
            getInitialCustomerUserSettings({
              notifyType: "update_ticket",
              notifyThrough: "email",
            }) || [],
          agent_app_desktop:
            getInitialCustomerUserSettings({
              notifyType: "update_ticket",
              notifyThrough: "agent_app_desktop",
            }) || [],
          agent_mobile_app:
            getInitialCustomerUserSettings({
              notifyType: "update_ticket",
              notifyThrough: "agent_mobile_app",
            }) || [],
          teams_app:
            getInitialCustomerUserSettings({
              notifyType: "update_ticket",
              notifyThrough: "teams_app",
            }) || [],
          notification_sound:
            getInitialCustomerUserNotificationSoundSettings("update_ticket") ??
            false,
        },
        closed_ticket: {
          email:
            getInitialCustomerUserSettings({
              notifyType: "closed_ticket",
              notifyThrough: "email",
            }) || [],
          agent_app_desktop:
            getInitialCustomerUserSettings({
              notifyType: "closed_ticket",
              notifyThrough: "agent_app_desktop",
            }) || [],
          agent_mobile_app:
            getInitialCustomerUserSettings({
              notifyType: "closed_ticket",
              notifyThrough: "agent_mobile_app",
            }) || [],
          teams_app:
            getInitialCustomerUserSettings({
              notifyType: "closed_ticket",
              notifyThrough: "teams_app",
            }) || [],
          notification_sound:
            getInitialCustomerUserNotificationSoundSettings("closed_ticket") ??
            false,
        },
        chat_ticket: {
          email:
            getInitialCustomerUserSettings({
              notifyType: "chat_ticket",
              notifyThrough: "email",
            }) || [],
          agent_app_desktop:
            getInitialCustomerUserSettings({
              notifyType: "chat_ticket",
              notifyThrough: "agent_app_desktop",
            }) || [],
          agent_mobile_app:
            getInitialCustomerUserSettings({
              notifyType: "chat_ticket",
              notifyThrough: "agent_mobile_app",
            }) || [],
          teams_app:
            getInitialCustomerUserSettings({
              notifyType: "chat_ticket",
              notifyThrough: "teams_app",
            }) || [],
          notification_sound:
            getInitialCustomerUserNotificationSoundSettings("chat_ticket") ??
            false,
        },
      },
    },
  };

  const methods = useForm({
    defaultValues,
  });

  const { reset } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [initialCustomerUserNotificationSettings]);

  const handleReset = async () => {
    try {
      if (isResetting) return false;
      await resetCustomerNotificationsSettings(customerId as string);

      toast.success("Notification settings reset successfully");
      return true;
    } catch (error) {
      toast.error("Unable to reset notification settings");
      return false;
    }
  };
  return (
    <FormProvider {...methods}>
      <Stack
        borderRadius={"8px"}
        bgcolor={theme.palette.neutral["010"]}
        height="100%"
      >
        <CustomerUserNotificationSettingsItems
          initialNotificationSettings={
            initialCustomerUserNotificationSettingsArr
          }
          isCustomSettings={
            initialCustomerUserNotificationSettings?.is_custom_settings
          }
          onResetClick={handleReset}
        />
      </Stack>
    </FormProvider>
  );
};
